import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'components/molecules/Chart';
import {
  formatDataValue,
  formatTickLabel,
} from 'utilities/chart';
import { getCssVars } from 'utilities/css';
import {
  getItemConcise,
  getItemLabel,
  itemProps,
  NULL_LABEL,
} from 'utilities/item';

const propTypes = {
  chartDirection: PropTypes.string.isRequired,
  displayOptions: PropTypes.arrayOf(
    PropTypes.shape({
      format: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  items: PropTypes.arrayOf(itemProps).isRequired,
  labelKey: PropTypes.string.isRequired,
};

const BAR_LABEL_SIZE = 12;
const DEFAULT_CHART_COLOR = getCssVars(['--purple-400']);
const SECONDARY_CHART_COLOR = getCssVars(['--purple-200']);

function getData(items, displayOptions, labelKey) {
  return displayOptions.reduce((axesData, { format, value: displayKey }, index) => {
    const axisData = items.reduce((all, item) => {
      const {
        colors,
        contents,
        ids,
        labels,
        values,
      } = all;

      const color = index === 0 ? DEFAULT_CHART_COLOR : SECONDARY_CHART_COLOR;
      const content = getItemConcise(item, displayKey);
      const id = item.id?.value;
      const label = getItemLabel(item, labelKey);
      const value = formatDataValue(item[displayKey]?.value, format);

      return {
        colors: [...colors, color],
        contents: [...contents, content],
        ids: [...ids, id],
        labels: [...labels, label],
        values: [...values, value],
      };
    }, {
      colors: [],
      contents: [],
      ids: [],
      labels: [],
      values: [],
    });

    return {
      ...axesData,
      [displayKey]: axisData,
    };
  }, {});
}

function getChartData(data, displayOptions, chartDirection) {
  const hasMultipleAxes = displayOptions.length > 1;

  return displayOptions.reduce((all, { label, value }, index) => {
    const axisData = data[value];
    const axisLetter = chartDirection === 'row' ? 'x' : 'y';
    const axisIdOption = { [`${axisLetter}AxisID`]: `${axisLetter}${index}` };

    return {
      ...all,
      datasets: [
        ...all.datasets,
        {
          ...axisIdOption,
          backgroundColor: axisData.colors,
          barPercentage: hasMultipleAxes ? 1.0 : 0.57,
          borderRadius: 3,
          categoryPercentage: hasMultipleAxes ? 0.7 : 1.0,
          contents: axisData.contents,
          data: axisData.values,
          hoverBackgroundColor: axisData.colors,
          label,
          tooltips: axisData.tooltips,
        },
      ],
    };
  }, {
    datasets: [],
    labels: data[displayOptions[0].value].labels,
  });
}

function getMaxValue(items, displayKey, format) {
  if (format === 'percent') return 1;

  const maxValue = items.reduce((max, item) => {
    const value = item[displayKey]?.value;

    return value > max ? value : max;
  }, -Infinity);

  // NOTE: If max is zero or -Infinity we return 1 so that the axes will show up
  return maxValue !== 0 && maxValue !== -Infinity ? maxValue : 1;
}

function getChartOptions(items, displayOptions, chartDirection) {
  // NOTE: When rendering a bar chart with multiple displayOptions, we will have two x axes. When
  // rendering a column chart with multiple displayOptions, we will have two y axes.
  const isBarChart = chartDirection === 'row';
  const indexAxis = isBarChart ? 'y' : 'x';
  const displayOptionAxis = isBarChart ? 'x' : 'y';

  return {
    indexAxis,
    layout: {
      padding: {
        right: 50,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        align: 'end',
        anchor: 'end',
        font: {
          size: BAR_LABEL_SIZE,
        },
        formatter: (value) => {
          if (value === undefined) {
            return NULL_LABEL;
          }

          return '';
        },
        textAlign: 'center',
      },
      legend: {
        labels: {
          generateLabels: ({ data: { datasets } }) => (
            datasets.map(({ backgroundColor, label }, index) => ({
              borderRadius: 2,
              datasetIndex: index,
              fillStyle: backgroundColor[0],
              lineWidth: 0,
              text: label,
            }))
          ),
        },
        onClick: () => {},
      },
      tooltip: {
        callbacks: {
          label: ({ dataset, dataIndex }) => dataset.contents[dataIndex],
          title: () => null,
        },
        displayColors: false,
        [isBarChart ? 'xAlign' : 'yAlign']: isBarChart ? 'left' : 'bottom',
      },
    },
    scales: {
      ...displayOptions.reduce((all, {
        format,
        label,
        value,
      }, index) => {
        const axisId = `${displayOptionAxis}${index}`;
        let position = index === 0 ? 'left' : 'right';
        if (chartDirection === 'row') {
          position = index === 0 ? 'bottom' : 'top';
        }

        return {
          ...all,
          [axisId]: {
            border: {
              display: false,
            },
            grid: {
              drawTicks: true,
            },
            max: getMaxValue(items, value, format),
            position,
            ticks: {
              beginAtZero: true,
              callback: (val) => formatTickLabel(val, format),
              count: 5,
            },
            title: {
              display: true,
              text: label,
            },
          },
        };
      }, {}),
      [indexAxis]: {
        border: {
          display: true,
        },
        grid: {
          display: false,
        },
        ticks: {
          rotation: 45,
        },
      },
    },
  };
}

function MultiBarChart({
  chartDirection,
  displayOptions,
  items,
  labelKey,
}) {
  return useMemo(() => {
    // @note: only recreate the chart when data changes so it doesn't re-run the animation
    const data = getData(items, displayOptions, labelKey);
    const chartData = getChartData(data, displayOptions, chartDirection);
    const chartOptions = getChartOptions(items, displayOptions, chartDirection);

    return (
      <Chart
        data={chartData}
        options={chartOptions}
        type="bar"
      />
    );
  }, [
    chartDirection,
    displayOptions,
    items,
    labelKey,
  ]);
}

MultiBarChart.propTypes = propTypes;

export default MultiBarChart;
